/**
 * The ⓘ on every page: a how-to video and an FAQ for that page (Akhil, 2026-09-30).
 *
 * Kept in the app for now — one entry per page, versioned with the code — and shaped as plain
 * data (`PageHelp`) so it can later be served by a Frappe doctype without the panel changing.
 * Each page's narrated walkthrough (recorded 2026-09-30) is a PUBLIC file on the Frappe site, streamed by full URL
 * (2026-10-01): the deployed proxy guards `/files/*`, and a bare `<video src>` carries no token. `video: null` is still a real state, and the panel says "coming soon"
 * rather than showing a broken player. Paste a YouTube / Loom / Vimeo link, or a file URL, into
 * `video` and the panel plays it — see `videoEmbed` for exactly which links are framed.
 *
 * The answers describe how each screen actually behaves today; when behaviour changes, the FAQ
 * beside it should change in the same commit.
 */

export interface PageFaq {
  q: string
  a: string
}

export interface PageHelp {
  title: string
  /** One line: what this page is for. */
  summary: string
  /** A video link, or `null` until one is recorded. */
  video: string | null
  faqs: PageFaq[]
}

export const PAGE_IDS = ['mail', 'requirements', 'rfq', 'suppliers', 'compare', 'quote', 'revisions', 'analytics', 'settings', 'users'] as const
export type PageId = (typeof PAGE_IDS)[number]

export const PAGE_HELP: Record<PageId, PageHelp> = {
  mail: {
    title: 'Mail',
    summary: 'The shared inbox: read customer enquiries and supplier replies, and turn an enquiry mail into an enquiry.',
    video: 'https://rfqdev.etoserp.com/files/mail.mp4',
    faqs: [
      {
        q: 'How do I create an enquiry from a mail?',
        a: 'Open the mail and check the lines read from its body and attachments. Choose the company first, then the enquiry type, delivery location and customer, and press Create. The lines go straight onto the new enquiry.',
      },
      {
        q: 'What do the coloured badges on a mail mean?',
        a: 'Green “✓ ENQ-…” — an enquiry was created from it by a person. Teal “⚡ auto” — Bosun created the enquiry automatically. Violet “reply” — a supplier answering one of our RFQs. “our RFQ” — a copy of our own outgoing RFQ. Click an enquiry badge to open it.',
      },
      {
        q: 'Why don’t I see every mail?',
        a: 'A purchaser sees the mail they are on (To or Cc); a purchase manager sees everything and can filter by “Addressed to”. The list also loads 100 mails at a time — scroll down and older mail loads automatically.',
      },
      {
        q: 'How do automatic enquiries work?',
        a: 'An enquiry mail to the inbound mailbox with a purchaser on Cc becomes a Draft enquiry on its own, usually within a minute, marked “auto”. The company is left for you to choose; check the header before sending RFQs.',
      },
      {
        q: 'An attachment was read wrongly or a line is missing — what now?',
        a: 'Create the enquiry anyway, then fix it on the RFQ builder: “Add item” lets you put a missing line at its own line number, and a line whose quantity could not be read arrives at 0, highlighted, for you to fill in.',
      },
    ],
  },

  requirements: {
    title: 'Requirements',
    summary: 'Every enquiry on one board, by stage — from new enquiry to won or lost.',
    video: 'https://rfqdev.etoserp.com/files/requirements.mp4',
    faqs: [
      {
        q: 'Why do I only see some enquiries?',
        a: 'A purchaser sees the enquiries assigned to them. A purchase manager sees all of them, and can switch to the purchaser’s view from Settings → My role to check what a purchaser sees.',
      },
      {
        q: 'How do I log an enquiry that did not come by mail?',
        a: 'Use New enquiry. Pick the company first — the customer search is scoped to it — then the type, delivery location and customer. You add the items on the next screen.',
      },
      {
        q: 'How do I mark a quotation won or lost?',
        a: 'On a card in “Quotation Sent”, choose Won or Rejected. Rejecting asks why it was lost and who won it; you can tick several reasons and competitors, or add new ones.',
      },
      {
        q: 'What does “Awaiting N days” mean?',
        a: 'How long the enquiry has sat in its current stage since its status last changed. It is a nudge, not an error.',
      },
    ],
  },

  rfq: {
    title: 'RFQ Builder',
    summary: 'Match each customer line to a catalogue item, settle units and suppliers, then send one RFQ per supplier.',
    video: 'https://rfqdev.etoserp.com/files/rfq.mp4',
    faqs: [
      {
        q: 'How do I change the item a line is matched to?',
        a: 'Click “change item” and pick from the catalogue. On a Provision enquiry the line takes the new item’s contract price and supplier if it has one; otherwise it goes to RFQ with no price, ready for suppliers.',
      },
      {
        q: 'Why is “Generate RFQs & send” greyed out?',
        a: 'Either the enquiry header is incomplete (vessel, port, sales person, customer reference and the like) or an RFQ line has no quantity yet. Hover the button: it names exactly what is missing.',
      },
      {
        q: 'The item is not sold in the customer’s unit — what do I do?',
        a: 'Pick one of the item’s units, or use the request link beside “customer asked in”. It works even when the unit exists elsewhere, such as PCS on an item sold only in Nos. Once the request is approved, the unit appears in that line’s unit switcher within about a minute, or press “check now”. The customer’s unit is shown here only, as its own label. It is never added to the printed description.',
      },
      {
        q: 'How do I add a line the reader missed, at the right number?',
        a: 'Use Add item and set “Line no.” to the customer’s number. The lines from that number on move down by one, so the RFQ and the quotation keep the customer’s own order.',
      },
      {
        q: 'What are contract lines?',
        a: 'Provision lines covered by a selected blanket order. They are priced at the contract rate directly and skip the RFQ; the ROUTE dropdown on the row lets you send one to RFQ instead.',
      },
    ],
  },

  suppliers: {
    title: 'Supplier Quotations',
    summary: 'Supplier replies to your RFQs, filed as supplier quotations ready for comparison.',
    video: 'https://rfqdev.etoserp.com/files/suppliers.mp4',
    faqs: [
      {
        q: 'How does a supplier’s reply get onto the enquiry?',
        a: 'Open the reply: its workbook is read into rates per line. Check them against the attachment in the side panel, then file it as a supplier quotation — that is what the comparison uses.',
      },
      {
        q: 'I filed a quotation with the wrong rates — can I fix it?',
        a: 'Yes. Filing again for the same RFQ discards the earlier draft and replaces it with the corrected one.',
      },
      {
        q: 'The supplier quoted in a different unit — is that a problem?',
        a: 'It is flagged as a unit mismatch. Check the conversion before comparing, so a price per box is not compared with a price per piece.',
      },
    ],
  },

  compare: {
    title: 'Quote Comparison',
    summary: 'Every supplier’s offer side by side, in the company currency, so you can choose who supplies what.',
    video: 'https://rfqdev.etoserp.com/files/compare.mp4',
    faqs: [
      {
        q: 'How are quotes in other currencies compared?',
        a: 'Each offer is converted to the company currency at the rates in Currency Exchange, so the columns compare like with like.',
      },
      {
        q: 'What do the strategy buttons do?',
        a: 'Cheapest, best availability, fewest suppliers and single supplier each preview a full allocation. Apply one as a starting point, then adjust individual lines.',
      },
      {
        q: 'Can one line be split between suppliers?',
        a: 'Yes — allocate part of the quantity to each. The line is then priced at the blended average of what each supplier supplies.',
      },
      {
        q: 'Can I price a line from our own stock?',
        a: 'Where the item is in stock, click the in-house stock badge on the line to price it from our own stock instead of a supplier.',
      },
    ],
  },

  quote: {
    title: 'Customer Quotation',
    summary: 'The priced quotation for the customer: margins, commissions, discount, taxes and the workbook to send.',
    video: 'https://rfqdev.etoserp.com/files/quote.mp4',
    faqs: [
      {
        q: 'How are the totals worked out?',
        a: 'Total is the sum of the lines; net total is the total less the discount; grand total is the net total plus taxes. Figures in the company currency are shown beside the customer’s where they differ.',
      },
      {
        q: 'Why can’t I download the quotation workbook?',
        a: 'A margin has to be set first — the download is held back so a quotation cannot go out at cost by mistake.',
      },
      {
        q: 'Can I show the customer their own currency?',
        a: 'Yes. Change the currency and the lines, discount and taxes are shown and edited in it, converted at the exchange rate on the quotation.',
      },
      {
        q: 'The quotation was sent outside the app — how do I record that?',
        a: 'Use “Mark as sent outside the app”. The enquiry moves to Quotation Sent as if it had gone from here.',
      },
    ],
  },

  revisions: {
    title: 'Revisions',
    summary: 'Re-quote a changed request by working on a copy, leaving the original untouched.',
    video: 'https://rfqdev.etoserp.com/files/revisions.mp4',
    faqs: [
      {
        q: 'When should I make a revision?',
        a: 'When the customer changes the request after a quotation has gone — more lines, fewer lines, different quantities. The copy carries the original’s header and lines.',
      },
      {
        q: 'Does the original enquiry change?',
        a: 'No. The revision is a separate enquiry with its own quotation; the original stays exactly as it was sent.',
      },
      {
        q: 'What can I change on the copy?',
        a: 'Add or remove lines and re-price them, then send the revised quotation as you would any other.',
      },
    ],
  },

  analytics: {
    title: 'Analytics',
    summary: 'How the pipeline is doing: conversion, win rate, blockers, value and each purchaser’s sourcing.',
    video: 'https://rfqdev.etoserp.com/files/analytics.mp4',
    faqs: [
      {
        q: 'Where do these numbers come from?',
        a: 'From the enquiries, RFQs, supplier quotations, comparisons and allocations themselves. Bulk-loaded accounts are left out, and the page says which, so the figures describe real work.',
      },
      {
        q: 'What counts as “blocked”?',
        a: 'An open enquiry whose status has not changed for three days or more. Most are drafts nobody took further; the RFQ Sent and Comparison Done ones are the real follow-ups.',
      },
      {
        q: 'What is the sourcing board telling me?',
        a: 'Shown to purchase managers. For each purchaser: how many suppliers they ask per enquiry, how often only one, whether the comparison is used, and which supplier they choose most. Amber is a signal to look, not a verdict — a category with one real supplier looks the same.',
      },
      {
        q: 'Why are some date ranges greyed out?',
        a: 'They would select exactly the same enquiries as “All time”, so they are disabled until there is enough history for them to mean something different.',
      },
    ],
  },

  settings: {
    title: 'Settings',
    summary: 'Your operating company and mailbox, pricing rules, category margins and how you view the app.',
    video: 'https://rfqdev.etoserp.com/files/settings.mp4',
    faqs: [
      {
        q: 'Are my company and mailbox remembered?',
        a: 'Yes — per person, on your own account, so they follow you across sign-ins and devices. Another user’s choices never affect yours.',
      },
      {
        q: 'What does “My role” change?',
        a: 'Only what you see. A manager can view the app as a purchaser to check their board and mailbox; nobody can choose a role above the one their account holds.',
      },
      {
        q: 'Who can change pricing rules and margins?',
        a: 'Anyone who can open Settings can edit them here, and they apply to everyone’s quotations — so treat them as shared settings: change them deliberately and tell the team.',
      },
    ],
  },

  users: {
    title: 'Users',
    summary: 'Who can sign in to Bosun, with which role and companies.',
    video: 'https://rfqdev.etoserp.com/files/users.mp4',
    faqs: [
      {
        q: 'How do I give someone access?',
        a: 'Add them here with their email, name, role and companies. They then sign in with that same email; their access comes from this record, so the two must match exactly.',
      },
      {
        q: 'What is the difference between the roles?',
        a: 'Sales sees their own customers’ enquiries. A purchaser works the enquiries assigned to them. A purchase manager sees everything, assigns purchasers to enquiries, and manages users.',
      },
      {
        q: 'How soon does a role change take effect?',
        a: 'The next time that person reloads the page or signs in. What they may do is checked by the server, so a removed role stops working within about a minute even if they never reload.',
      },
    ],
  },
}

export function helpFor(pageId: string): PageHelp | null {
  return (PAGE_IDS as readonly string[]).includes(pageId) ? PAGE_HELP[pageId as PageId] : null
}

export type VideoEmbed = { kind: 'iframe'; src: string } | { kind: 'file'; src: string } | { kind: 'link'; src: string }

/**
 * How the panel shows a video link. Only three known players are FRAMED — YouTube (through its
 * privacy-enhanced domain), Loom and Vimeo. A direct video file plays in the browser's own player;
 * anything else becomes a plain link opened in a new tab, because framing an arbitrary site
 * inside the app is how a help link turns into somebody else's page wearing ours.
 */
export function videoEmbed(url: string | null): VideoEmbed | null {
  const raw = (url ?? '').trim()
  if (raw === '') return null
  if (/\.(mp4|webm|mov)(\?.*)?$/i.test(raw)) return { kind: 'file', src: raw }
  const youtube = /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|shorts\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/.exec(raw)
  if (youtube) return { kind: 'iframe', src: `https://www.youtube-nocookie.com/embed/${youtube[1]}` }
  const loom = /loom\.com\/(?:share|embed)\/([A-Za-z0-9]+)/.exec(raw)
  if (loom) return { kind: 'iframe', src: `https://www.loom.com/embed/${loom[1]}` }
  const vimeo = /vimeo\.com\/(?:video\/)?(\d+)/.exec(raw)
  if (vimeo) return { kind: 'iframe', src: `https://player.vimeo.com/video/${vimeo[1]}` }
  return { kind: 'link', src: raw }
}

export type VideoSource = { from: 'app' | 'frappe' | 'url'; src: string }

/**
 * Where a direct video file is loaded from (2026-09-30).
 *
 *   - `app`    — shipped with the build under `public/help/`. A static asset on every deployment
 *                (dev server, Waves, Frappe-hosted), needing no sign-in; prefixed with the build's
 *                base path so a build served under a sub-path still finds it.
 *   - `frappe` — uploaded to the ERP (`/files/…`, `/private/files/…`). Permission-checked: a bare
 *                `<video src>` sends no credentials and gets the 401 every attachment used to, so
 *                the panel fetches it the authorised way first.
 *   - `url`    — anywhere else, played as given.
 */
export function videoSource(path: string, base: string): VideoSource {
  const p = path.trim()
  if (/^https?:\/\//i.test(p)) return { from: 'url', src: p }
  if (/^\/(private\/)?files\//.test(p)) return { from: 'frappe', src: p }
  const root = base.endsWith('/') ? base : `${base}/`
  return { from: 'app', src: root + p.replace(/^\/+/, '') }
}
